import { CallableInput, CallableToggle } from "Components/Shared";
import CallableDDIInput from "Components/Shared/CallableDDIInput";
import CallableDDITextArea from "Components/Shared/CallableDDITextArea";
import CallableTextArea from "Components/Shared/CallableTextArea";
import CNOSelect from "Components/Shared/CNOSelect";
import { Field, Formik } from "formik";
import { useAccount, useTicket } from "Hooks/Endpoints";
import React from "react";
import {
  Button,
  Container,
  Dimmer,
  Form,
  Header,
  Icon,
  Loader,
  Menu,
  Segment,
} from "semantic-ui-react";
import * as yup from "yup";

const validation = yup.object().shape({
  customerId: yup.string().required(),
  cno: yup.string().required(),
  contactName: yup.string().required(),
  contactTelephone: yup.string().required(),
  contactMobile: yup.string().required(),
  oline1: yup.string().required(),
  oline2: yup.string().required(),
  olevel1: yup.string().required(),
  olevel2: yup.string().required(),
  opostalCode: yup.string().required(),
  ocountry: yup.string().required(),
  nline1: yup.string().required(),
  nline2: yup.string().required(),
  nlevel1: yup.string().required(),
  nlevel2: yup.string().required(),
  npostalCode: yup.string().required(),
  ncountry: yup.string().required(),
  mbn: yup.string().required(),
  loa: yup.mixed().required(),
  bill: yup.mixed().required(),
});

type PortNumberFormProps = {
  onBack: Function;
};

const PortNumberForm = ({ onBack }: PortNumberFormProps) => {
  const { account } = useAccount();

  const { savePort, portCreatedResult, completePort, loading } = useTicket();

  const initValues = {
    file: null,
    customerId: account.id,
    takeover: false,
    oline1: "",
    oline2: "",
    olevel1: "",
    olevel2: "",
    opostalCode: "",
    ocountry: "",
    contactMobile: ""
  };

  const buildAddress = (
    setFieldValue: Function,
    values: any,
    prefix: string
  ) => {
    setFieldValue(prefix + "line1", values.line1);
    setFieldValue(prefix + "line2", values.line2);
    setFieldValue(prefix + "level1", values.level1);
    setFieldValue(prefix + "level2", values.level2);
    setFieldValue(prefix + "postalCode", values.postalCode);
    setFieldValue(prefix + "country", values.country);
  };

  const clearAddress = (setFieldValue: Function, prefix: string) => {
    setFieldValue(prefix + "line1", "");
    setFieldValue(prefix + "line2", "");
    setFieldValue(prefix + "level1", "");
    setFieldValue(prefix + "level2", "");
    setFieldValue(prefix + "postalCode", "");
    setFieldValue(prefix + "country", "");
  };

  if (portCreatedResult) {
    return (
      <Segment placeholder>
        <Header icon as='h2'>
          <Icon color='green' name='check square outline' />
          <p>Port Request #{portCreatedResult.caseId} has been created</p>
        </Header>

        <Button.Group>
          <Button
            secondary
            onClick={() => {
              completePort();
              onBack();
            }}
          >
            Close
          </Button>
        </Button.Group>
      </Segment>
    );
  }
  return (
    <Container textAlign='left' fluid>
      <Dimmer active={loading} inverted>
        <Loader inverted />
      </Dimmer>
      <Menu fluid stackable>
        <Menu.Item header onClick={() => onBack()}>
          <Icon name='x' />
        </Menu.Item>
        <Menu.Item header>Number Import Request</Menu.Item>
      </Menu>
      <Segment>
        <Formik
          initialValues={initValues}
          onSubmit={(values) => {
            savePort(values);
          }}
          validationSchema={validation}
          render={({
            values,
            handleSubmit,
            setFieldValue,
            validateForm,
            isValid,
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <h2>Numbers</h2>

                <CNOSelect name='cno' label='Current Network Operator' />

                <Field
                  name='mbn'
                  label='Main Billing Number - (Textbox will auto format to E164)'
                  component={CallableDDIInput}
                />

                <Field
                  name='associatedDDIs'
                  label='Associated Numbers - (Textbox will auto format to E164)'
                  component={CallableDDITextArea}
                />

                <Field
                  name='rangeDDIs'
                  label='Associated Range - (Textbox will auto format to E164)'
                  component={CallableDDITextArea}
                />

                <Field
                  name='specialNotes'
                  label='Enter any special instructions'
                  component={CallableTextArea}
                />

                <h2>Contact Details</h2>

                <Field
                  name='contactName'
                  label='Contact Name'
                  component={CallableInput}
                />

                <Field
                  name='contactPosition'
                  label='Contact Position'
                  component={CallableInput}
                />

                <Field
                  name='contactTelephone'
                  label='Contact Telephone'
                  component={CallableInput}
                />

                <Field
                  name='contactMobile'
                  label='Contact Mobile'
                  component={CallableInput}
                />

                <h2>Original Site Details </h2>

                <Field
                  name='sameAsAccount'
                  label='Same As Account Address'
                  component={CallableToggle}
                  onChange={(val: any, data: any) => {
                    if (data.checked) {
                      const { address } = account;
                      buildAddress(setFieldValue, address, "o");
                    } else {
                      clearAddress(setFieldValue, "o");
                    }
                    setTimeout(() => {
                      validateForm();
                    }, 50);
                  }}
                />

                <Field
                  name='oline1'
                  label='Premises'
                  component={CallableInput}
                />

                <Field name='oline2' label='Street' component={CallableInput} />

                <Field name='olevel2' label='Town' component={CallableInput} />

                <Field
                  name='olevel1'
                  label='Province'
                  component={CallableInput}
                />

                <Field
                  name='opostalCode'
                  label='PostCode'
                  component={CallableInput}
                />

                <Field
                  name='ocountry'
                  label='Country'
                  component={CallableInput}
                />

                <h2>New Site Details</h2>

                <Field
                  name='sameAsAccount'
                  label='Same As Account Address'
                  component={CallableToggle}
                  onChange={(val: any, data: any) => {
                    if (data.checked) {
                      const { address } = account;
                      buildAddress(setFieldValue, address, "n");
                    } else {
                      clearAddress(setFieldValue, "n");
                    }
                    setTimeout(() => {
                      validateForm();
                    }, 50);
                  }}
                />

                <Field
                  name='nline1'
                  label='Premises'
                  component={CallableInput}
                />

                <Field name='nline2' label='Street' component={CallableInput} />

                <Field name='nlevel2' label='Town' component={CallableInput} />

                <Field
                  name='nlevel1'
                  label='State / Province'
                  component={CallableInput}
                />

                <Field
                  name='npostalCode'
                  label='Postal Code'
                  component={CallableInput}
                />

                <Field
                  name='ncountry'
                  label='Country'
                  component={CallableInput}
                />

                <Segment>
                  <label htmlFor='loa' className='fileUpload'>
                    Select a valid signed Letter Of Authority
                  </label>
                  <input
                    id='loa'
                    name='loa'
                    type='file'
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      if (event.currentTarget.files) {
                        setFieldValue("loa", event.currentTarget.files[0]);
                      }
                    }}
                    className='form-control'
                  />
                </Segment>

                <Segment>
                  <label htmlFor='bill' className='fileUpload'>
                    Select a latest bill
                  </label>
                  <input
                    id='bill'
                    name='bill'
                    type='file'
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      if (event.currentTarget.files) {
                        setFieldValue("bill", event.currentTarget.files[0]);
                      }
                    }}
                    className='form-control'
                  />
                </Segment>

                <Segment>
                  <label htmlFor='takeover' className='fileUpload'>
                    If Company Takeover, please provide evidence
                  </label>
                  <input
                    id='takeover'
                    name='takeover'
                    type='file'
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      if (event.currentTarget.files) {
                        setFieldValue("takeover", event.currentTarget.files[0]);
                      }
                    }}
                    className='form-control'
                  />
                </Segment>

                <Button primary disabled={!isValid}>
                  Submit
                </Button>
              </Form>
            );
          }}
        />
      </Segment>
    </Container>
  );
};

export default PortNumberForm;
